// Presentational "why this number" drawer for forecast cells. All data is
// resolved by the page (artifact `_effective` query or legacy snapshot rows);
// this component only renders it plus the review-state actions.
import type { QuickBooksFpaForecastLineMetadata } from '../../../data/ServerApiClient'
import { formatAsOf, formatCurrencyCompact, formatSignedCurrency, trimStr } from './cfoFormat'
import {
  backtestPlainLanguage,
  modelFamilyPlainLanguage,
  parseDriverDecomposition,
  type ForecastReviewState,
} from './forecastExplain'
import type { UnderstandingClaim } from './cfoUnderstanding'

export type CfoForecastExplainHistoryPoint = {
  period: string
  value: number | null
}

export type CfoForecastExplainDrawerProps = {
  nodeLabel: string
  period: string
  scenarioKey: string
  line: QuickBooksFpaForecastLineMetadata | null
  loading?: boolean
  limitedDetail?: boolean
  error?: string
  history?: CfoForecastExplainHistoryPoint[]
  reviewBusy?: boolean
  reviewStatus?: string
  /** Active Client Understanding beliefs (plan Part 4); forecast.* beliefs are surfaced as the
   * provenance behind this forecast. Optional — the drawer is unchanged when omitted. */
  claims?: UnderstandingClaim[]
  onSetReviewState?: (state: ForecastReviewState) => void
  onClose: () => void
}

const REVIEW_ACTIONS: Array<{ state: ForecastReviewState; label: string; glyph: string }> = [
  { state: 'approved', label: 'Approve', glyph: '✓' },
  { state: 'flagged', label: 'Flag', glyph: '⚑' },
  { state: 'needs_discussion', label: 'Needs discussion', glyph: '…' },
]

const evidenceFieldEntries = (entry: Record<string, any>): Array<[string, string]> =>
  Object.entries(entry)
    .filter(([key, value]) => key !== 'source' && value != null && typeof value !== 'object')
    .slice(0, 6)
    .map(([key, value]) => [key.replace(/[_-]+/g, ' '), String(value)])

const evidenceReferenceLabel = (entry: Record<string, any>): string => {
  const sourceType = trimStr(entry.source_type || entry.doc_type || '')
  const sourceId = trimStr(entry.source_id || entry.invoice_id || entry.bill_id || entry.doc_number || '')
  if (/invoice/i.test(sourceType) || trimStr(entry.invoice_id)) return `Invoice ${sourceId || 'reference'}`
  if (/bill/i.test(sourceType) || trimStr(entry.bill_id)) return `Bill ${sourceId || 'reference'}`
  return ''
}

const HistorySparkline = ({ history }: { history: CfoForecastExplainHistoryPoint[] }) => {
  const values = history.map((point) => point.value).filter((value): value is number => value != null && Number.isFinite(value))
  if (values.length < 2) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const points = history
    .map((point, index) => {
      if (point.value == null || !Number.isFinite(point.value)) return null
      const x = history.length > 1 ? (index / (history.length - 1)) * 100 : 0
      const y = 36 - ((point.value - min) / span) * 32
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .filter(Boolean)
    .join(' ')
  return (
    <svg className="cfo-forecast-explain-sparkline" viewBox="0 0 100 40" preserveAspectRatio="none" role="img" aria-label="Trailing actual history">
      <polyline points={points} fill="none" />
    </svg>
  )
}

export function CfoForecastExplainDrawer({
  nodeLabel,
  period,
  scenarioKey,
  line,
  loading = false,
  limitedDetail = false,
  error = '',
  history = [],
  reviewBusy = false,
  reviewStatus = '',
  claims = [],
  onSetReviewState,
  onClose,
}: CfoForecastExplainDrawerProps) {
  const forecastBeliefs = claims.filter(
    (claim) => claim.directiveKind.startsWith('forecast.') && claim.status === 'active',
  )
  const drivers = parseDriverDecomposition(line?.driver_json)
  const backtestLine = backtestPlainLanguage(line?.backtest_json)
  const rawEvidence = line?.evidence_json
  const evidenceEntries = (Array.isArray(rawEvidence) ? rawEvidence : rawEvidence ? [rawEvidence] : [])
    .filter((entry: any) => entry && typeof entry === 'object') as Record<string, any>[]
  const periodLabel = formatAsOf(period)
  const lowValue = line?.low_value
  const highValue = line?.high_value
  const reviewState = trimStr(line?.feedback_state)
  return (
    <aside className="cfo-forecast-explain-drawer" role="dialog" aria-label={`Why ${nodeLabel} for ${periodLabel}`}>
      <header className="cfo-forecast-explain-head">
        <div>
          <span className="cfo-forecast-explain-eyebrow">Why this number</span>
          <h3>{nodeLabel}</h3>
          <p>{periodLabel} · {scenarioKey} scenario</p>
        </div>
        <button type="button" className="btn icon" aria-label="Close forecast explanation" onClick={onClose}>×</button>
      </header>
      {limitedDetail ? (
        <div className="cfo-shell-notice" role="status">
          Limited detail: this forecast is rendered from legacy package rows, so only the published line metadata is available.
        </div>
      ) : null}
      {error ? <div className="cfo-shell-notice error" role="status">{error}</div> : null}
      {loading ? (
        <p className="cfo-forecast-explain-muted" role="status">Loading line detail…</p>
      ) : !line ? (
        <p className="cfo-forecast-explain-muted" role="status">
          No published line detail exists for this row and period — derived and rollup rows are computed from other lines.
        </p>
      ) : (
        <>
          <section aria-label="What it is">
            <h4>What it is</h4>
            <p className="cfo-forecast-explain-value">{formatCurrencyCompact(line.final_value)}</p>
            {lowValue != null && highValue != null ? (
              <p className="cfo-forecast-explain-muted">Range {formatCurrencyCompact(lowValue)} – {formatCurrencyCompact(highValue)}</p>
            ) : null}
          </section>
          {drivers.length ? (
            <section aria-label="Why">
              <h4>Why</h4>
              <ul className="cfo-forecast-explain-drivers">
                {drivers.map((driver) => (
                  <li key={driver.key}>
                    <span>{driver.label}</span>
                    <b>{formatSignedCurrency(driver.amountImpact)}</b>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <section aria-label="Track record">
            <h4>Track record</h4>
            <p>Built with a {modelFamilyPlainLanguage(line.model_family)}.</p>
            {backtestLine ? <p className="cfo-forecast-explain-muted">{backtestLine}</p> : null}
          </section>
          {forecastBeliefs.length ? (
            <section aria-label="Beliefs">
              <h4>Beliefs shaping this forecast</h4>
              <ul className="cfo-forecast-explain-beliefs">
                {forecastBeliefs.map((belief) => (
                  <li key={belief.id}>{belief.assertion}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {history.length >= 2 ? (
            <section aria-label="History">
              <h4>History</h4>
              <HistorySparkline history={history} />
              <p className="cfo-forecast-explain-muted">
                {formatAsOf(history[0]?.period)} – {formatAsOf(history[history.length - 1]?.period)} actuals
              </p>
            </section>
          ) : null}
          {evidenceEntries.length ? (
            <section aria-label="Evidence">
              <h4>Evidence</h4>
              <ul className="cfo-forecast-explain-evidence">
                {evidenceEntries.map((entry, index) => {
                  const referenceLabel = evidenceReferenceLabel(entry)
                  return (
                    <li key={`${trimStr(entry.source) || 'evidence'}-${index}`}>
                      <b>{trimStr(entry.source) || referenceLabel || 'evidence'}</b>
                      {referenceLabel && trimStr(entry.source) ? <span className="tag">{referenceLabel}</span> : null}
                      <dl>
                        {evidenceFieldEntries(entry).map(([key, value]) => (
                          <div key={key}>
                            <dt>{key}</dt>
                            <dd>{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </li>
                  )
                })}
              </ul>
            </section>
          ) : null}
          {onSetReviewState ? (
            <section aria-label="Review">
              <h4>Review</h4>
              <div className="cfo-forecast-explain-review">
                {REVIEW_ACTIONS.map((action) => (
                  <button
                    key={action.state}
                    type="button"
                    className={reviewState === action.state ? 'btn active' : 'btn'}
                    aria-pressed={reviewState === action.state}
                    disabled={reviewBusy}
                    onClick={() => onSetReviewState(action.state)}
                  >
                    {action.glyph} {action.label}
                  </button>
                ))}
              </div>
              {reviewStatus ? <p className="cfo-forecast-explain-muted" role="status">{reviewStatus}</p> : null}
            </section>
          ) : null}
        </>
      )}
    </aside>
  )
}
